/* eslint-disable no-undef */
import React, { useCallback, useEffect, useRef, useState } from "react";
import TinyMCE from "react-tinymce";

import ResponsiveHelper from "src/legacy/services/responsive.helper";
import CollectionWidgetManager from "src/utils/collections.widget.service";
import { useSelector } from "react-redux";

import _ from "lodash";
import cx from "classnames";
//eslint-disable-next-line
import contentStyle from "./editorContent.scss";
import styles from "./editor.module.scss";
import DealRoomWidgetManager from "src/utils/dealRoom.widget.service";

const DefaultAssetIcon = "/images/assets/default_asset.jpg";
const DefaultCollectionIcon = "/images/collections/background.jpeg";


const apiUrl = process.env.REACT_APP_API_URL;

const getCollection = (entities) => {
  if (_.size(entities) === 0) {
    return null;
  }

  return entities.find((entity) => entity.entityType === "collection");
};

const getDealRoom = (entities) => {
  if (_.size(entities) === 0) {
    return null;
  }

  return entities.find((entity) => entity.entityType === "deal_room");
};

const getAssets = (entities) => {
  if (_.size(entities) === 0) {
    return null;
  }

  const collection = getCollection(entities);
  if (collection) {
    const assets = _.get(collection, "assets") || [];
    if (_.size(assets) > 0) {
      return assets.map((item) => item.asset || item);
    }
  }
  
  const dealRoom = getDealRoom(entities);
  if (dealRoom) {
    const assets = _.get(dealRoom, "assets") || [];
    if (_.size(assets) > 0) {
      return assets.map((item) => item.asset);
    }
  }

  return entities.filter((entity) => entity.entityType === "asset");
};


const getSections = (entities) => {
  if (_.size(entities) === 0) {
    return null;
  }

  const collection = getCollection(entities);
  const dealRoom = getDealRoom(entities);

  if (collection) {
    return _.get(collection, 'sections', []);
  }

  return _.get(dealRoom, 'sections', []);
}


const ComposeEditor = ({editorId, emailConfigState, applyWidgetToMessage, onChange, processAssets, getCollectionIcon}) => {
  const entities = emailConfigState.addAsset.entities;
  const collection = getCollection(entities);
  const dealRoom = getDealRoom(entities);
  const assets = getAssets(entities);
  const sections = getSections(entities);

  const user = useSelector((state) => _.get(state, "users.me"));
  const theme = useSelector((state) => _.get(state, "accounts.item.theme"));

  const editorRef = useRef(null);
  const isDealRoom = _.get(entities,"[0].entityType") === "deal_room";
  const widgetManager = isDealRoom ? new DealRoomWidgetManager() : new CollectionWidgetManager();

  const generateConfig = () => {
    var selection_toolbar =
      "bold italic | alignleft aligncenter alignright | numlist bullist | fontselect | fontsizeselect | forecolor backcolor";

    let validVariables = widgetManager.variables().map((item) => {
      return item.id;
    });

    var isMobile = ResponsiveHelper.isMobile();

    if (isMobile) {
      selection_toolbar = " ";
    }

    return {
      toolbar: false,
      branding: false,
      statusbar: false,
      contextmenu: false,
      theme: "silver",
      inline: true,
      plugins:
        "code, noneditable, variable, widget, autolink, link, lists, paste, quickbars",
      menubar: false,
      quickbars_insert_toolbar: false,
      quickbars_selection_toolbar: selection_toolbar,
      fontsize_formats:
        "5pt 5.5pt 6.5pt 7.5pt 8pt 9pt 10pt 10.5pt 11pt 12pt 14pt 16pt 18pt 20pt 22pt 24pt 26pt 28pt 36pt 48pt 72pt",
      anchor_top: false,
      anchor_bottom: false,
      resize: false,
      min_height: emailConfigState.height,
      height: emailConfigState.height,
      content_css: [
        contentStyle,
        "https://fonts.googleapis.com/css?family=Montserrat:100,100i,200,200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i",
      ],
      extended_valid_elements: "p[style|class],br",
      forced_root_block: false,
      force_br_newlines: true,
      force_p_newlines: false,
      valid_elements: "*[*]",
      browser_spellcheck: true,
      paste_convert_word_fake_lists: false,
      paste_retain_style_properties:
        "color font-size border border-collapse border-width border-color border-style background background-color",
      paste_webkit_styles:
        "color font-size border border-collapse border-width border-color border-style background background-color",
      noneditable_editable_class: "mceEditable",
      remove_trailing_brs: false,
      convert_urls: false,
      relative_urls: false,
      remove_script_host: false,
      variable_mapper: (id) => {
        let variable = widgetManager.variables().find((item) => {
          return item.id == id;
        });

        if (variable) {
          return variable.getValue(editorState);
        }
      },
      variable_valid: validVariables,
      widget_mapper: (id, existingNode) => {
        var widget = widgetManager.widget(id);
        if (widget) {
          widget.render(existingNode, editorState);
          onChange(tinyMCE.activeEditor.getContent());
        }
      },
      setup: (editor) => {
        editor.on('PostRender', () => {
          editorRef.current = editor;
        });
        editor.on("init", () => {
          // Noticed two editor instances are created for PPTX asset.
          // Choosing one editor instance
          if (editorId != editor.id) {
            return;
          }

          editor.execCommand("pfWidgetUpdate", false);

          editor.on("SetContent", () => {
            editor.execCommand("pfWidgetUpdate", false);
          });

          editor.ui.registry.addButton("collectionWidget", {
            icon: "image",
            tooltip: "Insert Collection as a card",
            onclick: () => {
              var widget = widgetManager.widget("collection.card");
              if (widget) {
                var template = widget.render(null, editorState);
                editor.execCommand(
                  "mceInsertContent",
                  false,
                  template.outerHTML,
                );
                editor.execCommand("pfWidgetUpdate", false, template);
              }
            },
          });

          editor.ui.registry.addButton("assetsWidget", {
            icon: "image",
            tooltip: "Insert assets as a table",
            onclick: () => {
              var widget = widgetManager.widget("assets.grid");
              if (widget) {
                var template = widget.render(null, editorState);
                editor.execCommand(
                  "mceInsertContent",
                  false,
                  template.outerHTML,
                );
                editor.execCommand("pfWidgetUpdate", false, template);
              }
            },
          });

          editor.on("keyup Change NodeChange", handleChange());

          editor.on("ExecCommand", (e) => {
            var selection = tinyMCE.activeEditor.selection.getContent();
            var el = document.createElement("html");
            el.innerHTML = "<head></head><body>" + selection + "</body>";
            var datapoints = Array.from(el.getElementsByClassName("variable"));
            if (datapoints.length > 0) {
              var styleToggle = function (key, value) {
                var criteria = datapoints
                  .map(function (datapoint) {
                    return datapoint.style[key] == value;
                  })
                  .reduce(function (a, b) {
                    return a && b;
                  });
                if (criteria) {
                  datapoints.forEach(function (datapoint) {
                    datapoint.style[key] = "";
                    datapoint.contentEditable = false;
                  });
                } else {
                  datapoints.forEach(function (datapoint) {
                    datapoint.style[key] = value;
                    datapoint.contentEditable = false;
                  });
                }
              };
              switch (e.command) {
                case "mceToggleFormat":
                  switch (e.value) {
                    case "bold":
                      styleToggle("font-weight", "bold");
                      break;
                    case "italic":
                      styleToggle("font-style", "italic");
                      break;
                    case "strikethrough":
                      styleToggle("text-decoration", "line-through");
                      break;
                    case "underline":
                      styleToggle("text-decoration", "underline");
                  }
                  tinyMCE.activeEditor.selection.setContent(
                    el.children[1].innerHTML,
                  );
                  break;
                case "mceApplyTextcolor":
                  styleToggle("color", e.value);
                  tinyMCE.activeEditor.selection.setContent(
                    el.children[1].innerHTML,
                  );
                  break;
                case "FontName":
                  styleToggle("font-family", e.value);
                  tinyMCE.activeEditor.selection.setContent(
                    el.children[1].innerHTML,
                  );
                  break;
                case "FontSize":
                  styleToggle("font-size", e.value);
                  tinyMCE.activeEditor.selection.setContent(
                    el.children[1].innerHTML,
                  );
                  break;
                case "RemoveFormat":
                  datapoints.forEach(function (datapoint) {
                    [
                      "font-weight",
                      "font-style",
                      "text-decoration",
                      "text-decoration",
                      "color",
                      "font-family",
                      "font-size",
                    ].forEach(function (key) {
                      datapoint.style[key] = "";
                    });
                    datapoint.contentEditable = false;
                  });
                  tinyMCE.activeEditor.selection.setContent(
                    el.children[1].innerHTML,
                  );
                  break;
              }
            }
          });
        });
      },
    };
  };

  const configRef = useRef(generateConfig());

  const [editorState, setEditorState] = useState({
    contact: {
      url: `${apiUrl}/api/2.0`,
    },
    message: emailConfigState.defaultMessage,
    collection: collection ?? dealRoom,
    collectionIcon: getCollectionIcon(collection ?? dealRoom),
    sections: sections || [],
    assets: processAssets(assets),
    theme: theme || { primaryColor: "#E51058" },
    emailSignature: user && user.emailSignature,
    config: generateConfig(),
  });

  const handleChange = (e) => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.getContent());
    }
  };

  useEffect(() => {
    //destroy the editor instance when component unmounts
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    // Update editorState with the new assets, collection, etc.
    setEditorState((prevState) => ({
      ...prevState,
      entities: entities,
      assets: processAssets(assets),
      collection: collection ?? dealRoom,
      collectionIcon: getCollectionIcon(collection ?? dealRoom),           
      sections: sections || []
    }));
    

    // generate the config for latest updates
    configRef.current = generateConfig();
    
    if (editorRef.current) {
      editorRef.current.execCommand("pfWidgetUpdate", false);
    }
  }, [emailConfigState.addAsset.entities, editorState.entities]);

  useEffect(() => {
    const widget = emailConfigState.widget;
    if (widget && editorRef.current) {

      const template = widget.render(null, editorState);

      editorRef.current.execCommand(
        "mceInsertContent",
        false,
        template.outerHTML,
      );
      editorRef.current.execCommand("pfWidgetUpdate", false, template);
    }
  }, [emailConfigState.widget]);  

  useEffect(() => {
    if (editorRef.current && emailConfigState.defaultMessage) {
      editorRef.current.setContent(emailConfigState.defaultMessage);
    }
  }, [emailConfigState.defaultMessage]);

  return (
    <div className={styles.editor} style={{ height: emailConfigState.height }}>
      <TinyMCE
        id={editorId}
        content={emailConfigState.defaultMessage}
        config={configRef.current}
        className={styles.mceLayer}
        onChange={handleChange}
      />
    </div>
  );
};

export default React.memo(ComposeEditor);